/**
 * PrinterFallbackModal — vises når en utskrift feiler fordi ingen printer er
 * konfigurert (ProfileReports, profil-felt eller Reports.PrinterName).
 * Lar brukeren velge en printer ad-hoc, og kallstedet kan deretter re-prøve
 * utskriften med valgt PrintNode-ID som override.
 *
 * Skal gjenbrukes av alle utskriftsstier (varemottak, plukk, lokasjon, SSCC, ...).
 */
import React, { useState } from 'react';
import { Printer, X, AlertCircle, Loader2 } from 'lucide-react';
import { PrinterSelect } from './PrinterSelect';

export interface PrinterFallbackModalProps {
  isOpen: boolean;
  /** Feilmelding fra backend (f.eks. "No printer configured for item labels"). */
  message?: string;
  /** Tittel på modalen. Default: "Velg skriver". */
  title?: string;
  onCancel: () => void;
  /** Kalles med valgt PrintNode-ID når brukeren bekrefter. Returnerer Promise så modalen kan vise spinner mens utskrift kjører. */
  onConfirm: (printerId: number) => Promise<void> | void;
}

export const PrinterFallbackModal: React.FC<PrinterFallbackModalProps> = ({
  isOpen,
  message,
  title = 'Velg skriver',
  onCancel,
  onConfirm,
}) => {
  const [selectedPrinterId, setSelectedPrinterId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    if (selectedPrinterId == null) return;
    setIsSubmitting(true);
    try {
      await onConfirm(selectedPrinterId);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2 text-gray-900">
            <Printer size={20} className="text-blue-600" />
            <h2 className="text-lg font-semibold">{title}</h2>
          </div>
          <button onClick={onCancel} disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50" type="button">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
            <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Ingen fast skriver konfigurert</p>
              {message && <p className="text-xs mt-1 text-amber-700">{message}</p>}
              <p className="text-xs mt-1">Velg en skriver under for å sende denne utskriften.</p>
            </div>
          </div>

          <PrinterSelect
            value={selectedPrinterId}
            onChange={(v) => setSelectedPrinterId(v ? parseInt(v, 10) : null)}
            valueField="id"
            label="Skriver"
            placeholder="-- Velg skriver --"
            disabled={isSubmitting}
            showTestButton={false}
            size="md"
          />
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
          >
            Avbryt
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting || selectedPrinterId == null}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : <Printer size={16} />}
            Skriv ut
          </button>
        </div>
      </div>
    </div>
  );
};
